import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { tailorHistoryApi, tailorsApi } from '../api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

function AdvanceModal({ tailorId, tailorName, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) {
      return toast.error('Please enter a valid amount');
    }
    setLoading(true);
    try {
      await tailorHistoryApi.giveAdvance({ tailorId, amount: Number(amount), date, description });
      toast.success('Advance given successfully!');
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to give advance'); }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Give Advance to {tailorName}</h3>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Amount (₹)</label>
              <input type="number" className="form-control" step="0.01" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter advance amount" />
            </div>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-control" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Description (Optional)</label>
              <textarea className="form-control" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional note" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Processing...' : 'Give Advance'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SettlementModal({ tailorId, periodData, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [type, setType] = useState(periodData.balance > 0 && periodData.advance > 0 ? 'settle' : 'settle');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const laborCost = Number(periodData.laborCost) || 0;
  const advanceGiven = Number(periodData.advance) || 0;
  const settlement = Number(periodData.settlement) || 0;
  const balance = Number(periodData.balance) || 0;

  const advanceToUse = balance > 0 && advanceGiven > 0 ? Math.min(advanceGiven, balance) : 0;
  const cashToPayAfterAdvance = balance > 0 ? Math.max(0, balance - advanceGiven) : balance;
  const remainingAdvanceAfterSettlement = advanceGiven - advanceToUse;
  const canDeductAdvance = balance > 0 && advanceGiven > 0;
  const advanceMoreThanBalance = advanceGiven > balance && balance > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) {
      return toast.error('Please enter a valid amount');
    }
    setLoading(true);
    try {
      if (type === 'useOnlyAdvance') {
        // Only deduct from advance (no cash payment)
        await tailorHistoryApi.deductFromAdvance({
          tailorId,
          amount: Number(amount),
          description: description || `Advance deducted for labor`
        });
        toast.success('Advance deducted! Remaining advance will be shown.');
      } else if (type === 'adjust' && canDeductAdvance) {
        // Use Advance + Pay Cash: record both settlement and advance deduction
        await tailorHistoryApi.settleWeekly({
          tailorId,
          amount: Number(amount),
          useAdvanceAmount: Math.min(advanceGiven, balance),
          description: description || `Paid using advance`,
          adjustAdvance: true
        });
        toast.success('Payment recorded! Advance used from previous advance.');
      } else {
        // Full cash payment
        await tailorHistoryApi.settleWeekly({
          tailorId,
          amount: Number(amount),
          useAdvanceAmount: 0,
          description: description || 'Cash payment',
          adjustAdvance: false
        });
        toast.success('Cash paid to tailor!');
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error('Settlement error:', err);
      toast.error(err.response?.data?.message || 'Failed to record settlement');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Settlement</h3>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="grid-2 mb-4" style={{ gap: '1rem' }}>
              <div className="card" style={{ background: 'var(--accent)', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                <div className="text-sm" style={{ color: 'white' }}>Total Labor</div>
                <div className="text-xl font-bold" style={{ color: 'white' }}>₹{laborCost.toLocaleString('en-IN')}</div>
              </div>
              <div className="card" style={{ background: 'var(--warning)', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
                <div className="text-sm" style={{ color: 'white' }}>Total Advance</div>
                <div className="text-xl font-bold" style={{ color: 'white' }}>₹{advanceGiven.toLocaleString('en-IN')}</div>
              </div>
            </div>

            <div className="card mb-4" style={{ background: balance >= 0 ? 'var(--success-bg)' : 'var(--danger-bg)', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
              <div className="text-sm text-muted">{balance >= 0 ? 'Tailor to Receive' : 'You to Receive'}</div>
              <div className="text-2xl font-bold" style={{ color: balance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                ₹{Math.abs(balance).toLocaleString('en-IN')}
              </div>
              {advanceGiven > 0 && balance > 0 && (
                <div className="text-sm" style={{ color: 'var(--warning)', marginTop: '0.5rem' }}>
                  (Advance given: ₹{advanceGiven.toLocaleString('en-IN')} will be used)
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">How to pay?</label>
              <div className="flex flex-col gap-2">
                {advanceMoreThanBalance && (
                  <label className="flex items-center gap-2 p-3 border rounded" style={{ borderColor: type === 'useOnlyAdvance' ? 'var(--primary)' : 'var(--border-color)', background: type === 'useOnlyAdvance' ? 'var(--primary-bg)' : 'transparent', cursor: 'pointer' }}>
                    <input type="radio" name="type" value="useOnlyAdvance" checked={type === 'useOnlyAdvance'} onChange={(e) => setType(e.target.value)} />
                    <div><div className="font-semibold">Use Only Advance</div><div className="text-xs text-muted">Deduct ₹{balance.toLocaleString('en-IN')} from advance (₹{remainingAdvanceAfterSettlement.toLocaleString('en-IN')} will remain)</div></div>
                  </label>
                )}
                {canDeductAdvance && !advanceMoreThanBalance && (
                  <label className="flex items-center gap-2 p-3 border rounded" style={{ borderColor: type === 'adjust' ? 'var(--primary)' : 'var(--border-color)', background: type === 'adjust' ? 'var(--primary-bg)' : 'transparent', cursor: 'pointer' }}>
                    <input type="radio" name="type" value="adjust" checked={type === 'adjust'} onChange={(e) => setType(e.target.value)} />
                    <div><div className="font-semibold">Use Advance + Pay Cash</div><div className="text-xs text-muted">Advance: ₹{advanceGiven.toLocaleString('en-IN')} + Cash: ₹{cashToPayAfterAdvance.toLocaleString('en-IN')}</div></div>
                  </label>
                )}
                <label className="flex items-center gap-2 p-3 border rounded" style={{ borderColor: type === 'settle' ? 'var(--success)' : 'var(--border-color)', background: type === 'settle' ? 'var(--success-bg)' : 'transparent', cursor: 'pointer' }}>
                  <input type="radio" name="type" value="settle" checked={type === 'settle'} onChange={(e) => setType(e.target.value)} />
                  <div><div className="font-semibold">Pay Full Cash</div><div className="text-xs text-muted">{canDeductAdvance ? `Pay ₹${balance.toLocaleString('en-IN')} in cash (advance kept for future)` : `Give cash to tailor`}</div></div>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Amount ({type === 'adjust' ? 'after using advance' : type === 'useOnlyAdvance' ? 'to deduct from advance' : 'to pay in cash'}) (₹)</label>
              <div className="flex gap-2 mb-2 flex-wrap">
                {type === 'useOnlyAdvance' && (
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setAmount(Math.abs(balance).toString())}>
                    Deduct: ₹{Math.abs(balance).toLocaleString('en-IN')}
                  </button>
                )}
                {type === 'adjust' ? (
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setAmount(Math.abs(cashToPayAfterAdvance).toString())}>
                    Pay: ₹{Math.abs(cashToPayAfterAdvance).toLocaleString('en-IN')}
                  </button>
                ) : (
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setAmount(Math.abs(balance).toString())}>
                    Full: ₹{Math.abs(balance).toLocaleString('en-IN')}
                  </button>
                )}
                {Math.abs(balance) >= 500 && type === 'settle' && <button type="button" className="btn btn-sm btn-secondary" onClick={() => setAmount(Math.floor(Math.abs(balance) / 2).toString())}>Half: ₹{Math.floor(Math.abs(balance) / 2).toLocaleString('en-IN')}</button>}
                <button type="button" className="btn btn-sm btn-outline" onClick={() => setAmount('')}>Clear</button>
              </div>
              <input type="number" className="form-control" step="1" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount" />
            </div>

            <div className="form-group">
              <label className="form-label">Note (Optional)</label>
              <textarea className="form-control" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional note" />
            </div>
          </div>
            <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Processing...' : 'Record Payment'}
            </button>
            </div>
        </form>
      </div>
    </div>
  );
}

export default function TailorHistory() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedTailorId = searchParams.get('tailorId');

  const [tailors, setTailors] = useState([]);
  const [historyData, setHistoryData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [advanceModal, setAdvanceModal] = useState(null);
  const [settlementModal, setSettlementModal] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const isSingleTailor = !!selectedTailorId;

  useEffect(() => {
    tailorsApi.getAll().then(res => setTailors(res.data.data || [])).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedTailorId) {
      setLoading(true);
      tailorHistoryApi.getByTailor(selectedTailorId)
        .then(res => setHistoryData(res.data.data))
        .catch(err => { console.error(err); toast.error('Failed to load tailor history'); })
        .finally(() => setLoading(false));

      tailorHistoryApi.getWeeklySettlement(selectedTailorId, 8)
        .then(res => setWeeklyData(res.data.data))
        .catch(err => console.error('Weekly settlement error:', err));
    } else {
      setLoading(true);
      tailorHistoryApi.getAll()
        .then(res => setHistoryData(res.data.data || []))
        .catch(err => { console.error(err); toast.error('Failed to load tailor history'); })
        .finally(() => setLoading(false));
    }
  }, [selectedTailorId]);

  const handleAdvance = () => {
    if (!selectedTailorId) return;
    const tailor = tailors.find(t => t.id === Number(selectedTailorId));
    setAdvanceModal({ tailorId: selectedTailorId, tailorName: tailor?.name });
  };

  const refreshData = () => {
    if (selectedTailorId) {
      tailorHistoryApi.getByTailor(selectedTailorId)
        .then(res => setHistoryData(res.data.data))
        .catch(console.error);
      tailorHistoryApi.getWeeklySettlement(selectedTailorId, 8)
        .then(res => setWeeklyData(res.data.data))
        .catch(console.error);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Tailor History</h1>
        {selectedTailorId && (
          <button className="btn btn-primary" onClick={handleAdvance}>+ Give Advance</button>
        )}
      </div>

      <div className="grid-4 mb-6">
        <div className="card">
          <div className="card-body">
            <select className="form-control" value={selectedTailorId || ''} onChange={(e) => navigate(e.target.value ? `/tailor-history?tailorId=${e.target.value}` : '/tailor-history')}>
              <option value="">All Tailors</option>
              {tailors.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : isSingleTailor && historyData && historyData.tailor ? (
        <>

          <div className="flex gap-2 mb-6">
            <button className={`btn ${activeTab === 'overview' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('overview')}>Overview</button>
            <button className={`btn ${activeTab === 'weekly' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('weekly')}>
              Settlement
            </button>
          </div>

          {activeTab === 'overview' && historyData && (
            <>
              <div className="grid-4 mb-6">
                <div className="card">
                  <div className="card-body text-center">
                    <div className="text-2xl font-bold">{historyData.stats.totalAssigned}</div>
                    <div className="text-sm text-muted">Total Assigned</div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body text-center">
                    <div className="text-2xl font-bold text-success">{historyData.stats.totalCompleted}</div>
                    <div className="text-sm text-muted">Completed</div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body text-center">
                    <div className="text-2xl font-bold text-accent">₹{Number(historyData.stats.completedLaborCost).toLocaleString('en-IN')}</div>
                    <div className="text-sm text-muted">Total Labor Cost</div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body text-center">
                    <div className="text-2xl font-bold text-warning">₹{Number(historyData.stats.totalAdvance).toLocaleString('en-IN')}</div>
                    <div className="text-sm text-muted">Advance (For Future Work)</div>
                  </div>
                </div>
              </div>

              <div className="grid-2 mb-6" style={{ gap: '1rem' }}>
                <div className="card" style={{ borderLeft: '4px solid var(--success)' }}>
                  <div className="card-body text-center">
                    <div className="text-3xl font-bold" style={{ color: 'var(--success)' }}>₹{Number(historyData.stats.cashBalance).toLocaleString('en-IN')}</div>
                    <div className="text-sm text-muted">Cash Balance (Labor - Cash Paid)</div>
                    <div className="text-xs text-muted">{historyData.stats.cashBalance >= 0 ? 'Pay to tailor' : 'Receive from tailor'}</div>
                  </div>
                </div>
                <div className="card" style={{ borderLeft: '4px solid var(--warning)' }}>
                  <div className="card-body text-center">
                    <div className="text-3xl font-bold" style={{ color: 'var(--warning)' }}>₹{Number(historyData.stats.totalAdvance).toLocaleString('en-IN')}</div>
                    <div className="text-sm text-muted">Advance (for future work)</div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3>Assignments</h3>
                </div>
                <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
                  <table>
                    <thead><tr><th>Order</th><th>Garment</th><th>Labor Cost</th><th>Status</th><th>Assigned</th></tr></thead>
                    <tbody>
                      {historyData.assignments?.length === 0 ? (
                        <tr><td colSpan={5}><div className="empty-state"><div className="empty-icon">📋</div><p>No assignments yet</p></div></td></tr>
                      ) : (historyData.assignments || []).map(a => (
                        <tr key={a.id}>
                          <td className="mono text-sm" style={{ color: 'var(--accent-light)' }}>{a.orderItem?.order?.orderNumber}</td>
                          <td>{a.orderItem?.garmentType}</td>
                          <td className="font-semibold">₹{Number(a.laborCost).toLocaleString('en-IN')}</td>
                          <td>
                            <span className={`badge ${a.isCompleted ? 'badge-success' : 'badge-warning'}`}>
                              {a.isCompleted ? 'Completed' : 'Active'}
                            </span>
                          </td>
                          <td className="text-sm text-muted">{new Date(a.assignedAt).toLocaleDateString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === 'weekly' && weeklyData && (
            <>
              <div className="grid-2 mb-6" style={{ gap: '1rem' }}>
                <div className="card" style={{ borderLeft: '4px solid var(--accent)' }}>
                  <div className="card-body text-center">
                    <div className="text-3xl font-bold text-accent">₹{Number(weeklyData.totals.totalLaborCost).toLocaleString('en-IN')}</div>
                    <div className="text-sm text-muted">Total Labor Cost</div>
                  </div>
                </div>
                <div className="card" style={{ borderLeft: '4px solid var(--warning)' }}>
                  <div className="card-body text-center">
                    <div className="text-3xl font-bold text-warning">₹{Number(weeklyData.totals.totalAdvance).toLocaleString('en-IN')}</div>
                    <div className="text-sm text-muted">Advance (For Future Work)</div>
                  </div>
                </div>
              </div>

              <div className="grid-2 mb-6" style={{ gap: '1rem' }}>
                <div className="card" style={{ borderLeft: '4px solid var(--info)' }}>
                  <div className="card-body text-center">
                    <div className="text-3xl font-bold text-info">₹{Number(weeklyData.totals.totalSettlement).toLocaleString('en-IN')}</div>
                    <div className="text-sm text-muted">Total Cash Paid</div>
                  </div>
                </div>
                <div className="card" style={{ borderLeft: `4px solid ${weeklyData.totals.cashBalance >= 0 ? 'var(--success)' : 'var(--danger)'}` }}>
                  <div className="card-body text-center">
                    <div className="text-3xl font-bold" style={{ color: weeklyData.totals.cashBalance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      ₹{Math.abs(weeklyData.totals.cashBalance).toLocaleString('en-IN')}
                    </div>
                    <div className="text-sm text-muted">{weeklyData.totals.cashBalance >= 0 ? 'Pay to Tailor' : 'Receive from Tailor'}</div>
                  </div>
                </div>
              </div>

              {weeklyData.totals.cashBalance !== 0 && (
                <div className="card mb-4">
                  <div className="card-body text-center">
                    <button
                      className={`btn btn-lg ${weeklyData.totals.cashBalance > 0 ? 'btn-primary' : 'btn-success'}`}
                      onClick={() => setSettlementModal({
                        tailorId: selectedTailorId,
                        periodData: {
                          laborCost: weeklyData.totals.totalLaborCost,
                          advance: weeklyData.totals.totalAdvance,
                          settlement: weeklyData.totals.totalSettlement,
                          balance: weeklyData.totals.cashBalance,
                          periodLabel: 'Total Settlement'
                        }
                      })}
                    >
                      {weeklyData.totals.cashBalance > 0 
                        ? (weeklyData.totals.totalAdvance > 0 
                            ? `Pay Cash: ₹${weeklyData.totals.cashBalance.toLocaleString('en-IN')} (Advance: ₹${weeklyData.totals.totalAdvance.toLocaleString('en-IN')})`
                            : `Pay Cash to Tailor: ₹${weeklyData.totals.cashBalance.toLocaleString('en-IN')}`
                          )
                        : `Receive: ₹${Math.abs(weeklyData.totals.cashBalance).toLocaleString('en-IN')}`
                      }
                    </button>
                  </div>
                </div>
              )}

              {weeklyData.recentPayments?.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h3>Recent Payments</h3>
                  </div>
                  <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklyData.recentPayments.map((p, idx) => (
                          <tr key={idx}>
                            <td>{new Date(p.paymentDate).toLocaleDateString('en-IN')}</td>
                            <td>
                              <span className={`badge ${p.type === 'advance' ? 'badge-warning' : 'badge-success'}`}>
                                {p.type === 'advance' ? 'Advance' : 'Settlement'}
                              </span>
                            </td>
                            <td className="font-semibold">₹{Math.abs(Number(p.amount)).toLocaleString('en-IN')}</td>
                            <td className="text-sm text-muted">{p.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

        </>
      ) : (
        <div className="grid-3">
          {Array.isArray(historyData) &&   historyData.map(h => (
            <div key={h.tailor.id} className="card" onClick={() => navigate(`/tailor-history?tailorId=${h.tailor.id}`)} style={{ cursor: 'pointer' }}>
              <div className="card-body">
                <div className="flex items-center gap-3 mb-4">
                  <div style={{ width: 48, height: 48, background: 'linear-gradient(135deg, var(--accent),#8b5cf6)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'white', fontSize: '1.2rem' }}>
                    {h.tailor.name[0]}
                  </div>
                  <div>
                    <div className="font-semibold">{h.tailor.name}</div>
                    <div className="text-xs text-muted">{h.tailor.phone}</div>
                  </div>
                </div>
                <div className="grid-2" style={{ gap: '0.75rem' }}>
                  <div style={{ background: 'var(--warning-bg)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius)', padding: '0.75rem', textAlign: 'center' }}>
                    <div className="text-xl font-bold" style={{ color: 'var(--warning)' }}>{h.stats.totalAssigned}</div>
                    <div className="text-xs text-muted">Assigned</div>
                  </div>
                  <div style={{ background: 'var(--success-bg)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 'var(--radius)', padding: '0.75rem', textAlign: 'center' }}>
                    <div className="text-xl font-bold" style={{ color: 'var(--success)' }}>{h.stats.totalCompleted}</div>
                    <div className="text-xs text-muted">Completed</div>
                  </div>
                </div>
                <div className="mt-4 grid-2" style={{ gap: '0.5rem' }}>
                  <div className="text-center">
                    <div className="text-xs text-muted">Labor Cost</div>
                    <div className="font-bold text-accent">₹{Number(h.stats.completedLaborCost).toLocaleString('en-IN')}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-muted">Cash Balance</div>
                    <div className="font-bold" style={{ color: h.stats.cashBalance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      ₹{Math.abs(h.stats.cashBalance).toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {Array.isArray(historyData) && historyData.length === 0 && (
            <div className="empty-state" style={{ gridColumn: '1/-1' }}><div className="empty-icon">✂️</div><p>No tailors found</p></div>
          )}
        </div>
      )}

      {advanceModal && (
        <AdvanceModal
          tailorId={advanceModal.tailorId}
          tailorName={advanceModal.tailorName}
          onClose={() => setAdvanceModal(null)}
          onSaved={() => refreshData()}
        />
      )}

      {settlementModal && (
        <SettlementModal
          tailorId={settlementModal.tailorId}
          periodData={settlementModal.periodData}
          onClose={() => setSettlementModal(null)}
          onSaved={() => refreshData()}
        />
      )}
    </div>
  );
}